import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { slideInFromRightWeighted } from '../../shared/component/Animation';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn } from '../../shared/component/headless/button/BtnBody';
import {
  buildQRGeneratorTextOptions,
  downloadQRPNG,
  getQRCodeDataUrl,
  type QRTextPosition,
} from '../../shared/util/getQRCode';
import { getConfig } from '../../shared/env/envManager';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getBtnName } from '../../shared/constants/btnNames';
import { paperTextureSmall } from '../../shared/component/paperTexture';
import QRGeneratorPreview from './QRGeneratorPreview';

const QRGeneratorModal: React.FC = () => {
  const { isClosing, clearModal } = useModalStore();
  const { setting } = useSetting();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);

  const [textPosition, setTextPosition] = useState<QRTextPosition>('below-logo');
  const [isTransparent, setIsTransparent] = useState<boolean>(true);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  const qrUrl = useMemo(() => {
    if (!pageOwner) return null;
    const config = getConfig();
    return `https://${config.appDomain}/${pageOwner.aui}`;
  }, [pageOwner?.aui]);

  const textOptions = useMemo(
    () => buildQRGeneratorTextOptions(setting?.username, textPosition),
    [setting?.username, textPosition],
  );

  useEffect(() => {
    if (!qrUrl) return undefined;
    let cancelled = false;
    setPreviewLoading(true);
    void getQRCodeDataUrl(qrUrl, textOptions, isTransparent).then((url) => {
      if (cancelled) return;
      setPreviewDataUrl(url);
      setPreviewLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [qrUrl, textOptions, isTransparent]);

  if (!pageOwner || !setting) return null;

  const handleGenerate = () => {
    if (!qrUrl) return;
    downloadQRPNG(qrUrl, `QR-${setting.username}.png`, textOptions, isTransparent);
    clearModal(400);
  };

  return (
    <LeftModalFrame
      onClick={(e) => e.stopPropagation()}
      $isClosing={isClosing}
    >
      <QRCodeGeneratorComp>
        <QRCodeGeneratorTitle>{getModalMessage('QR_CODE_OPTIONS_TITLE')}</QRCodeGeneratorTitle>
        <QRPreviewWrapper>
          <QRGeneratorPreview
            dataUrl={previewDataUrl}
            isTransparent={isTransparent}
            loading={previewLoading}
          />
        </QRPreviewWrapper>
        <OptionContainer>
          <OptionSection>
            <OptionLabel>{getModalMessage('USERNAME_PLACEMENT')}</OptionLabel>
            <RadioGroup>
              <CheckboxComp>
                <Checkbox
                  type="radio"
                  name="textPosition"
                  checked={textPosition === 'below-logo'}
                  onChange={() => setTextPosition('below-logo')}
                />
                {getModalMessage('QR_BELOW_LOGO')}
              </CheckboxComp>
              <CheckboxComp>
                <Checkbox
                  type="radio"
                  name="textPosition"
                  checked={textPosition === 'below-qr'}
                  onChange={() => setTextPosition('below-qr')}
                />
                {getModalMessage('QR_BELOW_QR')}
              </CheckboxComp>
              <CheckboxComp>
                <Checkbox
                  type="radio"
                  name="textPosition"
                  checked={textPosition === 'none'}
                  onChange={() => setTextPosition('none')}
                />
                {getModalMessage('QR_OFF')}
              </CheckboxComp>
            </RadioGroup>
          </OptionSection>
          <OptionSection>
            <OptionLabel>{getModalMessage('BACKGROUND')}</OptionLabel>
            <RadioGroup>
              <CheckboxComp>
                <Checkbox
                  type="radio"
                  name="background"
                  checked={isTransparent === true}
                  onChange={() => setIsTransparent(true)}
                />
                {getModalMessage('TRANSPARENT')}
              </CheckboxComp>
              <CheckboxComp>
                <Checkbox
                  type="radio"
                  name="background"
                  checked={isTransparent === false}
                  onChange={() => setIsTransparent(false)}
                />
                {getModalMessage('WHITE')}
              </CheckboxComp>
            </RadioGroup>
          </OptionSection>
        </OptionContainer>
        <ButtonContainer>
          <HeadlessBtn
            value={getBtnName('GENERATE')}
            handleClick={handleGenerate}
            StyledBtn={BlackFitBtn}
          />
        </ButtonContainer>
      </QRCodeGeneratorComp>
    </LeftModalFrame>
  );
};

const LeftModalFrame = styled.div<{ $isClosing: boolean }>`
  position: absolute;
  right: clamp(20px, 2vw, 30px);
  bottom: calc(8vh);
  width: 12vw;
  min-width: 230px;
  max-height: min(85vh, 550px);
  height: fit-content;

  padding: 20px;

  opacity: ${({ $isClosing }) => ($isClosing ? 0 : 1)};
  transform: ${({ $isClosing }) =>
    $isClosing
      ? 'translateX(100%)'
      : 'translateX(0)'
  };

  transition: all 0.6s ease-out;
  ${paperTextureSmall}
  ${slideInFromRightWeighted}
`;

const QRPreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
`;

const QRCodeGeneratorComp = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: clamp(12px, 2vh, 20px);
`;

const QRCodeGeneratorTitle = styled.div`
  ${({ theme }) => theme.typography.Body_02_1};
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OptionLabel = styled.div`
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxComp = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Checkbox = styled.input.attrs({ type: 'radio' })`
  appearance: none;
  width: 12px;
  height: 12px;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  margin-right: 10px;
  outline: none;
  transition: border 0.2s, background 0.2s;
  position: relative;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked {
    border-color: ${({ theme }) => theme.colors.color_Gray_03};
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: -3px;
    width: 5px;
    height: 10px;
    border: solid ${({ theme }) => theme.colors.color_Gray_02};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;


export default QRGeneratorModal;
